import { presentLexError } from '@/lib/user-notice';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  getAllHighlights,
  deleteHighlight,
  updateHighlight,
  formatHighlightDate,
  type Highlight,
} from '@/lib/highlights';

type FilterType = 'all' | 'highlight' | 'favorite';
type FavoritesView = 'lex' | 'dof';

export function HighlightsPage() {
  const [highlights, setHighlights] = useState<Highlight[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editNote, setEditNote] = useState('');
  const [filter, setFilter] = useState<FilterType>('all');
  const [favoritesView, setFavoritesView] = useState<FavoritesView>('lex');

  const load = async () => {
    setError('');
    try {
      const options = filter === 'all' ? undefined : { type: filter };
      const data = await getAllHighlights(options);
      const sorted = [...data].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setHighlights(sorted);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar favoritos.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    load();
  }, [filter]);

  const handleDelete = async (id: string) => {
    if (!confirm('¿Eliminar este elemento guardado?')) return;
    setDeletingId(id);
    try {
      await deleteHighlight(id);
      setHighlights((prev) => prev.filter((h) => h.id !== id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al eliminar.');
    } finally {
      setDeletingId(null);
    }
  };

  const startEdit = (h: Highlight) => {
    setEditingId(h.id);
    setEditNote(h.note ?? '');
  };

  const saveEdit = async () => {
    if (editingId == null) return;
    try {
      await updateHighlight(editingId, { note: editNote || undefined });
      setHighlights((prev) =>
        prev.map((h) => (h.id === editingId ? { ...h, note: editNote || null, updatedAt: new Date().toISOString() } : h))
      );
      setEditingId(null);
      setEditNote('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al guardar.');
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditNote('');
  };

  const stripHtml = (html: string) => html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  const formatMetaDate = (value?: string) => {
    if (!value) return '';
    try {
      return new Date(value).toLocaleDateString('es-MX', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return '';
    }
  };

  const visibleHighlights = highlights.filter((h) => {
    if (favoritesView === 'dof') return h.itemType === 'dof_publication';
    return h.itemType !== 'dof_publication';
  });

  return (
    <main className="lex-page">
      {/* Page title */}
      <div style={{ padding: '4px 0 16px' }}>
        <p style={{ margin: 0, fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>
          Mis notas
        </p>
        <h1 style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em', color: 'var(--color-text)' }}>
          Guardados
        </h1>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* Tabs: filter type */}
        <div style={{ display: 'flex', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 10, padding: 3, gap: 3 }}>
          {(['all', 'highlight', 'favorite'] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              style={{
                flex: 1,
                padding: '6px 10px',
                borderRadius: 8,
                border: 'none',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
                background: filter === f ? 'var(--color-navy)' : 'transparent',
                color: filter === f ? 'var(--color-bg)' : 'var(--color-text-muted)',
                transition: 'background 0.15s, color 0.15s',
              }}
            >
              {f === 'all' ? 'Todos' : f === 'highlight' ? 'Resaltados' : 'Favoritos'}
            </button>
          ))}
        </div>

        {/* Tabs: source (Lex / DOF) */}
        <div style={{ display: 'flex', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 10, padding: 3, gap: 3 }}>
          {([
            ['lex', 'DUMAS'],
            ['dof', 'DOF'],
          ] as const).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setFavoritesView(value)}
              style={{
                flex: 1,
                padding: '6px 10px',
                borderRadius: 8,
                border: 'none',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
                background: favoritesView === value ? 'var(--color-navy)' : 'transparent',
                color: favoritesView === value ? 'var(--color-bg)' : 'var(--color-text-muted)',
                transition: 'background 0.15s, color 0.15s',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Error */}
        {error ? (
          <p style={{ margin: 0, color: 'var(--color-danger)', fontSize: 14 }}>{presentLexError(error)}</p>
        ) : null}

        {/* Loading */}
        {loading ? (
          <p style={{ color: 'var(--color-text-muted)', fontSize: 14, textAlign: 'center', padding: '24px 0', margin: 0 }}>
            Cargando guardados…
          </p>
        ) : visibleHighlights.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)', fontSize: 14, textAlign: 'center', padding: '24px 0', margin: 0 }}>
            {favoritesView === 'dof'
              ? 'No tienes publicaciones DOF guardadas.'
              : 'No tienes contenido DUMAS guardado.'}
          </p>
        ) : (
          /* Highlights list — border-bottom rows */
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {visibleHighlights.map((h) => (
              <article
                key={h.id}
                style={{
                  padding: '16px 0',
                  borderBottom: '1px solid var(--color-border-soft)',
                }}
              >
                {/* Meta row: type badge + source info */}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
                  {/* Type: law code / source in accent */}
                  {h.itemType === 'dof_publication' ? (
                    <>
                      {h.scjnMetadata?.source ? (
                        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-accent)' }}>
                          {h.scjnMetadata.source}
                        </span>
                      ) : (
                        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-accent)' }}>
                          DOF
                        </span>
                      )}
                      {h.scjnMetadata?.date ? (
                        <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{formatMetaDate(h.scjnMetadata.date)}</span>
                      ) : null}
                    </>
                  ) : (
                    <>
                      {h.articleNumber ? (
                        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-accent)' }}>
                          Art. {h.articleNumber}
                        </span>
                      ) : null}
                      {h.articleTitle ? (
                        <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{h.articleTitle}</span>
                      ) : null}
                    </>
                  )}
                  {/* Type label */}
                  <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--color-text-muted)' }}>
                    {h.type === 'favorite' ? 'Favorito' : 'Resaltado'}
                  </span>
                  {/* Date */}
                  <span style={{ fontSize: 11, color: 'var(--color-text-faint)', marginLeft: 'auto' }}>
                    {formatHighlightDate(h.createdAt)}
                  </span>
                </div>

                {/* Excerpt */}
                <p style={{ fontSize: 14, color: 'var(--color-text)', margin: '0 0 10px', lineHeight: 1.55, fontStyle: 'italic' }}>
                  "{stripHtml(h.text).slice(0, 220)}{h.text.length > 220 ? '…' : ''}"
                </p>

                {/* Note editing */}
                {editingId === h.id ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <input
                      type="text"
                      value={editNote}
                      onChange={(e) => setEditNote(e.target.value)}
                      placeholder="Nota personal"
                      className="lex-input"
                    />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button type="button" className="lex-button-primary" onClick={saveEdit}>
                        Guardar
                      </button>
                      <button type="button" className="lex-button-secondary" onClick={cancelEdit}>
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Personal note */}
                    {h.note ? (
                      <div style={{ marginBottom: 10, padding: '8px 12px', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8 }}>
                        <p style={{ margin: '0 0 2px', fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>
                          Nota personal
                        </p>
                        <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-muted)', lineHeight: 1.5 }}>{h.note}</p>
                      </div>
                    ) : null}

                    {/* Actions — plain text links */}
                    <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                      {h.itemType === 'dof_publication' ? (
                        h.scjnMetadata?.url ? (
                          <a
                            href={h.scjnMetadata.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-accent)', textDecoration: 'none' }}
                          >
                            Ver publicación ↗
                          </a>
                        ) : null
                      ) : h.lawId && h.articleId ? (
                        <Link
                          to={`/leyes/${h.lawId}/articulo/${h.articleId}`}
                          style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-accent)', textDecoration: 'none' }}
                        >
                          Ver artículo →
                        </Link>
                      ) : null}

                      <button
                        type="button"
                        onClick={() => startEdit(h)}
                        style={{ padding: 0, border: 'none', background: 'none', fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)', cursor: 'pointer' }}
                      >
                        Editar nota
                      </button>

                      <button
                        type="button"
                        onClick={() => handleDelete(h.id)}
                        disabled={deletingId === h.id}
                        style={{ padding: 0, border: 'none', background: 'none', fontSize: 12, fontWeight: 600, color: 'var(--color-danger)', cursor: 'pointer' }}
                      >
                        {deletingId === h.id ? 'Eliminando…' : 'Eliminar'}
                      </button>
                    </div>
                  </>
                )}
              </article>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
